<template>
  <!--
    Only the standalone flow sends a limitType (429 hourly/daily). The booking flow
    reaches this component via a per-booking 403 hard cap with no limitType, so it
    must keep the generic "not available" copy — the standalone retry/contact copy
    would be misleading there.
  -->
  <NotAvailable v-if="!message" />

  <ContentPanel v-else>
    <ContentWrapper centered-content large-padding class="content-block">
      <IconContainer>
        <FhIcon
          size="md"
          color="warning-default"
          :icon="alertFilled"
          data-test-id="warning-icon"
        />
      </IconContainer>

      <FhText tag="h1" variant="display-xl" data-test-id="reached-limit-title">
        {{ message.title }}
      </FhText>

      <FhText variant="body-md" data-test-id="reached-limit-description">
        {{ message.description }}
      </FhText>
    </ContentWrapper>
  </ContentPanel>

  <EventInfoBlock :booking="props.booking" :company="props.company" />
</template>

<script setup>
import { computed } from 'vue';
import { FhIcon, FhText } from '@fareharbor-com/beacon-vue';
import { alertFilled } from '@fareharbor-com/beacon-vue/icons';
import ContentPanel from '../shared/ContentPanel.vue';
import ContentWrapper from '../shared/ContentWrapper.vue';
import IconContainer from '../shared/IconContainer.vue';
import EventInfoBlock from '../shared/EventInfoBlock.vue';
import NotAvailable from './NotAvailable.vue';
import { LIMIT_TYPE } from '../../constants/limitTypes';

const props = defineProps({
  booking: {
    type: Object,
    default: () => ({}),
  },
  company: {
    type: Object,
    default: () => ({}),
  },
  // Standalone-only: 'daily' = per-company hard cap for the day; 'hourly' = transient
  // per-link burst. Empty/unknown (e.g. the booking flow) falls back to NotAvailable.
  limitType: {
    type: String,
    default: '',
  },
});

// The daily cap is a dead end for today, so we point the signer at the operator.
// The hourly limit is transient, so we ask them to retry shortly. Anything else
// returns null and the template renders the generic NotAvailable message.
const message = computed(() => {
  if (props.limitType === LIMIT_TYPE.DAILY) {
    return {
      title: T('Waiver unavailable today'),
      description: T(
        'This waiver can’t accept any more submissions today. Please contact the operator for assistance.',
      ),
    };
  }

  if (props.limitType === LIMIT_TYPE.HOURLY) {
    return {
      title: T('Please try again in a few minutes'),
      description: T(
        'This waiver is temporarily unavailable due to a high number of requests. Please try again in a few minutes.',
      ),
    };
  }

  return null;
});
</script>

<style scoped>
.content-block {
  gap: var(--fh-size-md);
}
</style>
